import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { X, FileText, Calendar, Star, Check, Sparkles, Maximize2, ChevronLeft, ChevronRight, Trash2, AlertTriangle } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { api } from '../../lib/api'
import { CategorizationForm } from './CategorizationForm'
import { useState, useEffect } from 'react'

interface ScanDetailModalProps {
  scanId: number
  isOpen: boolean
  onClose: () => void
  studentId?: number // For parent view
  isParentView?: boolean
}

interface ScanDetail {
  scan: {
    id: number
    scan_type: string | null
    subject: string | null
    title: string | null
    grade_detected: number | null
    grade_source: string | null
    xp_awarded: number
    image_path: string | null
    is_friday_folder: boolean
    friday_folder_id: number | null
    ocr_text: string | null
    quality_score: number | null
    created_at: string
    processed_at: string | null
    needs_categorization: boolean
    has_scanned_document: boolean
  }
  linked_assignment: {
    id: number
    name: string
    course_name: string | null
    due_at: string | null
    score: number | null
    grade: string | null
  } | null
  review: {
    reviewed: boolean
    reviewed_at: string | null
    xp_awarded: number
  } | null
}

export function ScanDetailModal({
  scanId,
  isOpen,
  onClose,
  studentId,
  isParentView = false,
}: ScanDetailModalProps) {
  const queryClient = useQueryClient()
  const [showSuccess, setShowSuccess] = useState(false)
  const [earnedXP, setEarnedXP] = useState(0)
  const [showFullscreen, setShowFullscreen] = useState(false)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [imageError, setImageError] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [imageLoading, setImageLoading] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const imageEndpointBase = isParentView && studentId
    ? `/api/parent/students/${studentId}/scans/${scanId}/image`
    : `/api/student/scans/${scanId}/image`

  // Fetch page info when modal opens
  useEffect(() => {
    if (!isOpen || !scanId) {
      setCurrentPage(1)
      setTotalPages(1)
      return
    }

    const fetchPageInfo = async () => {
      try {
        const response = await api.get(`${imageEndpointBase}?info=true`)
        setTotalPages(response.data.total_pages || 1)
        setCurrentPage(1)
      } catch (err) {
        console.error('Failed to get page info:', err)
        setTotalPages(1)
      }
    }

    fetchPageInfo()
  }, [isOpen, scanId, imageEndpointBase])

  // Fetch image for current page
  useEffect(() => {
    let currentUrl: string | null = null

    if (!isOpen || !scanId) {
      setImageUrl(null)
      setImageError(false)
      return
    }

    const fetchImage = async () => {
      setImageLoading(true)
      try {
        const response = await api.get(`${imageEndpointBase}?page=${currentPage}`, {
          responseType: 'blob',
        })

        // Create blob URL from response
        const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/png' })
        currentUrl = URL.createObjectURL(blob)
        setImageUrl(currentUrl)
        setImageError(false)
      } catch (err) {
        console.error('Failed to load scan image:', err)
        setImageError(true)
      } finally {
        setImageLoading(false)
      }
    }

    fetchImage()

    // Cleanup blob URL when component unmounts or page changes
    return () => {
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl)
      }
    }
  }, [isOpen, scanId, currentPage, imageEndpointBase])

  const goToPrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1)
  }

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1)
  }

  const endpoint = isParentView && studentId
    ? `/api/parent/students/${studentId}/scans/${scanId}`
    : `/api/student/scans/${scanId}`

  const { data, isLoading, error } = useQuery<ScanDetail>({
    queryKey: ['scan-detail', scanId, studentId],
    queryFn: async () => {
      const response = await api.get(endpoint)
      return response.data
    },
    enabled: isOpen,
  })

  const categorizeMutation = useMutation({
    mutationFn: async (categorizeData: { subject?: string; scan_type?: string; title?: string }) => {
      const categorizeEndpoint = isParentView && studentId
        ? `/api/parent/students/${studentId}/scans/${scanId}/categorize`
        : `/api/student/scans/${scanId}/categorize`
      const response = await api.patch(categorizeEndpoint, categorizeData)
      return response.data
    },
    onSuccess: (result) => {
      if (result.xp_awarded > 0) {
        setEarnedXP(result.xp_awarded)
        setShowSuccess(true)
        setTimeout(() => setShowSuccess(false), 3000)
      }
      queryClient.invalidateQueries({ queryKey: ['scan-detail', scanId] })
      queryClient.invalidateQueries({ queryKey: ['scan-history'] })
      queryClient.invalidateQueries({ queryKey: ['scan-stats'] })
      queryClient.invalidateQueries({ queryKey: ['student-dashboard'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!isParentView || !studentId) {
        throw new Error('Delete only available for parents')
      }
      const response = await api.delete(`/api/parent/students/${studentId}/scans/${scanId}`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scan-history'] })
      queryClient.invalidateQueries({ queryKey: ['scan-stats'] })
      queryClient.invalidateQueries({ queryKey: ['student-dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['child-scans'] })
      setShowDeleteConfirm(false)
      onClose()
    },
  })

  if (!isOpen) return null

  return (
    <AnimatePresence>
      {/* Fullscreen Image Viewer */}
      {showFullscreen && imageUrl && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/95 p-4"
          onClick={() => setShowFullscreen(false)}
        >
          <button
            onClick={() => setShowFullscreen(false)}
            className="absolute top-4 right-4 p-2 rounded-lg bg-surface-card/80 hover:bg-surface-hover transition-colors z-10"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={imageUrl}
            alt={`Scan page ${currentPage} fullscreen`}
            className={`max-w-full max-h-full object-contain ${imageLoading ? 'opacity-50' : ''}`}
            onClick={(e) => e.stopPropagation()}
          />

          {/* Fullscreen page navigation */}
          {totalPages > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); goToPrevPage(); }}
                disabled={currentPage <= 1}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-lg bg-surface-card/80 hover:bg-surface-hover transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); goToNextPage(); }}
                disabled={currentPage >= totalPages}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-lg bg-surface-card/80 hover:bg-surface-hover transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-8 h-8" />
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-surface-card/80 text-lg">
                Page {currentPage} of {totalPages}
              </div>
            </>
          )}
        </motion.div>
      )}

      {/* Main Modal */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-surface-card rounded-xl w-full max-w-4xl max-h-[95vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-700/50">
            <h2 className="text-lg font-bold">Scan Details</h2>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-surface-hover transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(95vh-60px)] p-6 space-y-6">
            {isLoading && (
              <div className="space-y-4 animate-pulse">
                <div className="h-48 bg-gray-700 rounded-lg" />
                <div className="h-6 bg-gray-700 rounded w-2/3" />
                <div className="h-4 bg-gray-700 rounded w-1/2" />
              </div>
            )}

            {error && (
              <div className="text-center py-8 text-red-400">
                Failed to load scan details
              </div>
            )}

            {data && (
              <>
                {/* Success toast */}
                <AnimatePresence>
                  {showSuccess && (
                    <motion.div
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="bg-green-900/50 border border-green-500/50 rounded-lg p-4 flex items-center gap-3"
                    >
                      <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                        <Check className="w-5 h-5 text-green-500" />
                      </div>
                      <div>
                        <p className="font-medium text-green-400">Saved!</p>
                        {earnedXP > 0 && (
                          <p className="text-sm text-green-300 flex items-center gap-1">
                            <Sparkles className="w-4 h-4" />
                            +{earnedXP} XP earned
                          </p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Image preview with page navigation */}
                <div className="relative rounded-lg overflow-hidden bg-surface-base group">
                  {imageUrl && !imageError ? (
                    <>
                      <img
                        src={imageUrl}
                        alt={`Scan page ${currentPage}`}
                        className={`w-full max-h-[50vh] object-contain cursor-pointer transition-opacity ${imageLoading ? 'opacity-50' : ''}`}
                        onClick={() => setShowFullscreen(true)}
                      />
                      {/* Fullscreen button overlay */}
                      <button
                        onClick={() => setShowFullscreen(true)}
                        className="absolute top-2 right-2 p-2 rounded-lg bg-black/50 hover:bg-black/70 transition-colors opacity-0 group-hover:opacity-100"
                      >
                        <Maximize2 className="w-5 h-5" />
                      </button>

                      {/* Page navigation controls */}
                      {totalPages > 1 && (
                        <>
                          {/* Previous page button */}
                          <button
                            onClick={(e) => { e.stopPropagation(); goToPrevPage(); }}
                            disabled={currentPage <= 1}
                            className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-lg bg-black/70 hover:bg-black/90 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ChevronLeft className="w-6 h-6" />
                          </button>

                          {/* Next page button */}
                          <button
                            onClick={(e) => { e.stopPropagation(); goToNextPage(); }}
                            disabled={currentPage >= totalPages}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg bg-black/70 hover:bg-black/90 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ChevronRight className="w-6 h-6" />
                          </button>

                          {/* Page indicator */}
                          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/70 text-sm">
                            Page {currentPage} of {totalPages}
                          </div>
                        </>
                      )}

                      {/* Loading overlay */}
                      {imageLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <div className="text-white">Loading...</div>
                        </div>
                      )}
                    </>
                  ) : imageError ? (
                    <div className="h-48 bg-gray-700 rounded-lg flex items-center justify-center">
                      <FileText className="w-12 h-12 text-gray-500" />
                      <p className="ml-2 text-gray-400">Image not available</p>
                    </div>
                  ) : (
                    <div className="h-48 bg-gray-700 rounded-lg flex items-center justify-center animate-pulse">
                      <div className="text-gray-500">Loading image...</div>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-lg">
                      {data.scan.title || data.scan.subject || data.scan.scan_type || 'Untitled Scan'}
                    </h3>
                    {data.scan.grade_detected !== null && (
                      <span className="text-lg font-bold text-quest-primary">
                        {data.scan.grade_detected}%
                      </span>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2 text-sm">
                    {/* Date & Time */}
                    <span className="flex items-center gap-1 text-gray-400">
                      <Calendar className="w-4 h-4" />
                      {new Date(data.scan.created_at).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </span>

                    {/* Friday folder */}
                    {data.scan.is_friday_folder && (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded">
                        <Star className="w-3 h-3" />
                        Friday Folder
                      </span>
                    )}

                    {/* XP earned */}
                    {data.scan.xp_awarded > 0 && (
                      <span className="text-quest-accent">
                        +{data.scan.xp_awarded} XP earned
                      </span>
                    )}
                  </div>
                </div>

                {/* Linked assignment info */}
                {data.linked_assignment && (
                  <div className="bg-green-900/20 border border-green-700/30 rounded-lg p-3">
                    <p className="text-xs text-gray-400 mb-1">Linked to Assignment</p>
                    <p className="font-medium text-green-400">{data.linked_assignment.name}</p>
                    <p className="text-sm text-gray-500">{data.linked_assignment.course_name}</p>
                    {data.linked_assignment.score !== null && (
                      <p className="text-sm text-quest-primary mt-1">
                        Score: {data.linked_assignment.score} / {data.linked_assignment.grade || 'N/A'}
                      </p>
                    )}
                  </div>
                )}

                {/* Categorization form */}
                <div className="border-t border-gray-700/50 pt-4">
                  <h4 className="font-medium mb-3">
                    {data.scan.needs_categorization ? 'Add Missing Info' : 'Edit Details'}
                  </h4>
                  <CategorizationForm
                    currentSubject={data.scan.subject}
                    currentScanType={data.scan.scan_type}
                    currentTitle={data.scan.title}
                    linkedAssignment={data.linked_assignment}
                    canLinkAssignment={data.scan.has_scanned_document && !isParentView}
                    onSave={(categorizeData) => categorizeMutation.mutate(categorizeData)}
                    isSaving={categorizeMutation.isPending}
                    showXPHint={!isParentView}
                  />
                  {isParentView && (
                    <p className="text-xs text-gray-500 mt-2">
                      Note: No XP awarded for parent help. Students earn XP by categorizing themselves.
                    </p>
                  )}
                </div>

                {/* OCR text (collapsed by default) */}
                {data.scan.ocr_text && (
                  <details className="border-t border-gray-700/50 pt-4">
                    <summary className="cursor-pointer text-sm text-gray-400 hover:text-white">
                      Show extracted text
                    </summary>
                    <pre className="mt-2 p-3 bg-surface-base rounded-lg text-xs text-gray-300 whitespace-pre-wrap max-h-48 overflow-y-auto">
                      {data.scan.ocr_text}
                    </pre>
                  </details>
                )}

                {/* Delete button (parent only) */}
                {isParentView && studentId && (
                  <div className="border-t border-gray-700/50 pt-4">
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="flex items-center gap-2 px-4 py-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete Scan
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </motion.div>

        {/* Delete Confirmation Dialog */}
        <AnimatePresence>
          {showDeleteConfirm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50"
              onClick={() => setShowDeleteConfirm(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-surface-card rounded-xl p-6 max-w-sm mx-4 border border-gray-700/50"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-red-900/30 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                  </div>
                  <h3 className="text-lg font-semibold">Delete Scan?</h3>
                </div>
                <p className="text-gray-400 mb-6">
                  This will permanently delete this scan and its image. This action cannot be undone.
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                    disabled={deleteMutation.isPending}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => deleteMutation.mutate()}
                    disabled={deleteMutation.isPending}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {deleteMutation.isPending ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </>
                    )}
                  </button>
                </div>
                {deleteMutation.isError && (
                  <p className="mt-3 text-sm text-red-400">
                    Failed to delete scan. Please try again.
                  </p>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  )
}
